import * as React                                 from 'react';
import { useTranslation }                         from 'react-i18next';
import { connect }                                from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { reset, SubmissionError }                 from 'redux-form';
import DV                                         from 'ts-data-validator';

import { staticPageConstants } from '../../../../common/constants/staticPageConstants';

import RenderButton        from '../../../components/buttons/base/RenderButton';
import ContactUs           from '../../../components/buttons/ContactUs';
import ConsultationBlock   from '../../../components/consultationBlock';
import Meta                from '../../../components/meta';
import Preloader           from '../../../components/containers/preloader';
import PageHeader          from '../../../components/pageHeader';
import { RenderBasicTabs } from '../../../components/tabs/RenderBasicTabs';
import RenderHeaderTabs    from '../../../components/tabs/RenderHeaderTabs';
import useIsMounted        from '../../../hooks/useIsMounted';
import { useCommon }       from '../../../providers/common';
import { useNotification } from '../../../providers/notification';
import { useTab }          from '../../../providers/tabs';
import { useUser }         from '../../../providers/user';
import { useWhereBuy }     from '../../../providers/whereBuy';

import renderStaticSection from '../../_common/utils/renderStaticSection';
import Notfound            from '../../notfound/components/Notfound';

import { guaranteeConstants } from '../constants';

import GuaranteeForm from './GuaranteeForm';

const Guarantee = function(props) {
  const history  = useHistory();
  const location = useLocation();
  const match    = useRouteMatch();
  const { t }    = useTranslation();

  const { dispatch } = props;

  const common       = useCommon();
  const notification = useNotification();
  const user         = useUser();
  const whereBuy     = useWhereBuy();
  const isMounted    = useIsMounted();

  const ref  = React.useRef(null);
  const TABS = React.useRef({
    ENTRANCE_DOORS:   0,
    INTERIOR_DOORS:   1,
    WARRANTY_SERVICE: 2,
  });
  const tabs = React.useRef({});

  const {
          registerTabsComponent,
          changeTab,
          ...tab
        }         = useTab();
  const activeTab = tab[guaranteeConstants.GUARANTEE_TABS] || 0;

  const handleChangeTab = React.useCallback((tabsType, index) => {
    if (ref.current) {
      if (tabsType === staticPageConstants.PAGE_TYPE.TABS_WITH_SCROLLING && tabs.current[index]) {
        const nav      = ref.current.ownerDocument
          .getElementById('nav')
          .getBoundingClientRect();
        const header   = ref.current.ownerDocument
          .getElementById('header')
          .getBoundingClientRect();
        const position = tabs.current[index].getBoundingClientRect();

        if (!isSSR()) {
          scrollTo({
            behavior: 'smooth',
            left:     0,
            top:      position.top - nav.height - header.height,
          });
        }
      }
    }
  }, [ref, tabs]);

  const hasInteriorPage = React.useMemo(() => {
    return common.pages.some((page) => page.template === 'guarantee_interior');
  }, [common.pages]);
  const initialValues   = React.useMemo(() => {
    const appealType = user.appealTypes.find((item) => item.aliasKey === 'GUARANTEE') || {};
    const _token     = user.tokenList.GUARANTEE || null;

    if (!_token) {
      return;
    }

    return {
      _token,
      aliasKey:     'GUARANTEE',
      appealTypeId: appealType.appealTypeId,
    };
  }, [user.appealTypes, user.tokenList.GUARANTEE]);
  const page            = React.useMemo(() => {
    return common.pages.find((page) => page.urlKey === match.params.page);
  }, [common.pages, match.params.page]);
  const content         = React.useMemo(() => {
    let content;

    try {
      content = JSON.parse(page.content);
    } catch {
    }

    return content;
  }, [page]);
  const headerProps     = React.useMemo(() => {
    const props = {
      bg:          page.headImage,
      transparent: DV.isString(page.headImage),
    };

    if (content && content.PAGE_TYPE !== staticPageConstants.PAGE_TYPE.STATIC_PAGE) {
      props.doubleBtns = (
        <RenderHeaderTabs
          active={0}
          nav={content.PAGE_TABS.map((label) => {
            return { label };
          })}
          onChange={(index) => handleChangeTab(content.PAGE_TYPE, index)}
        />
      );
    }

    return props;
  }, [content, handleChangeTab, page]);
  const meta            = page?.meta;

  const getInitialState = React.useCallback(() => {
    if ((!page || (!page.hasOwnProperty('content') && !page.hasOwnProperty('headImage'))) && !common.loading) {
      common.getPageData(location.pathname);
    }

    if (!whereBuy.initialStateLoaded && !whereBuy.loading) {
      whereBuy.getInitialState();
    }
  }, [common.loading, common.pages, location.pathname, page, whereBuy.initialStateLoaded, whereBuy.loading]);
  const submitForm      = React.useCallback((event, handleSubmit) => {
    event.preventDefault();

    handleSubmit((values) => {
      const validateValues = {
        ...values,
        ...initialValues,
        phone: values.phone.replace(/\s*\*$/g, ''),
      };

      return user.send(validateValues)
        .then(() => {
          notification.showModal(t('notification.success_title'), t('notification.success_description'));

          dispatch(reset('GuaranteeForm'));
        })
        .catch(errors => {
          notification.showModal(t('notification.something_went_wrong_title'), t('notification.something_went_wrong_description.refresh_pages'));

          throw new SubmissionError(errors);
        });
    })(event);
  }, [dispatch, initialValues]);
  const renderSections  = React.useCallback((content) => {
    if (content.PAGE_TYPE === staticPageConstants.PAGE_TYPE.STATIC_PAGE) {
      return content.SECTIONS.map((section, index) => renderStaticSection(section, index));
    }

    return content.SECTIONS.map((tab, index) => {
      return (
        <div
          key={index}
          ref={(node) => {
            tabs.current[index] = node;
          }}
        >
          {tab.map((section, index) => renderStaticSection(section, index))}
        </div>
      );
    });
  }, []);

  const formProps = React.useMemo(() => {
    const props = {
      allTradeStore: whereBuy.items,
      submitForm,
    };

    if (!!initialValues) {
      props.initialValues = initialValues;
    }

    return props;
  }, [initialValues, submitForm, whereBuy.items]);

  React.useEffect(() => {
    if (common.pages.find(page => page.template === 'guarantee_interior')?.urlKey === match.params.page) {
      changeTab(guaranteeConstants.GUARANTEE_TABS, 1);
    }

    if (DV.isNull(activeTab)) {
      registerTabsComponent(guaranteeConstants.GUARANTEE_TABS);
    }

    if (!user.tokenList.hasOwnProperty('GUARANTEE')) {
      getInitialState();
    }

    scrollTo({
      left: 0,
      top:  0,
    });
  }, []);
  React.useEffect(() => {
    if (!common.loading && !user.tokenList.GUARANTEE) {
      user.getToken('GUARANTEE');
    }
  }, [common.loading, user.tokenList.GUARANTEE]);

  if (common.loading || user.appealLoading || whereBuy.loading || user.appealWait) {
    return <Preloader />;
  }

  if (!content) {
    return <Notfound />;
  }

  return (
    <div
      className={'page-content'}
      ref={ref}
    >
      <Meta {...meta} />
      {DV.isNotEmptyString(page.label) && <PageHeader {...headerProps}>{page.label}</PageHeader>}
      {page.hasOwnProperty('content') && page.hasOwnProperty('headImage') && (
        <>
          <main className={page.template}>
            <section className={'withTabs'}>
              <RenderBasicTabs
                active={activeTab}
                changeTab={(index) => {
                  const template = common.pages.some(page => page.template === 'guarantee_interior') && index === 1
                    ? 'guarantee_interior'
                    : 'guarantee';
                  const page     = common.pages.find(page => page.template === template);

                  if (page) {
                    const url = `/${page.urlKey}`;

                    if (page.urlKey !== match.params.page) {
                      history.push(url);
                      common.getPageData(url);
                    }
                  }

                  changeTab(guaranteeConstants.GUARANTEE_TABS, index);
                }}
                forceTabs
                nav={[
                  { label: t('client_guarantee_entrance') },
                  ...(hasInteriorPage ? [{ label: t('client_guarantee_interior') }] : []),
                  { label: t('client_warrantyService') },
                ]}
              >
                <div>
                  <article>{renderSections(content)}</article>
                  <section className={'to-catalog'}>
                    <div className={'container'}>
                      <RenderButton
                        className={'fill'}
                        label={t('client_goToCatalog')}
                        type={'link'}
                        uri={'/vhodnye-dveri'}
                      />
                      <ContactUs label={page.contactButtonName} />
                    </div>
                  </section>
                </div>
                {hasInteriorPage && (
                  <div>
                    <article>{renderSections(content)}</article>
                    <section className={'to-catalog'}>
                      <div className={'container'}>
                        <RenderButton
                          className={'fill'}
                          label={page.inCatalogLabel || t('client_goToCatalog')}
                          type={'link'}
                          uri={page.inCatalogLink || '/vhodnye-dveri'}
                        />
                        <ContactUs />
                      </div>
                    </section>
                  </div>
                )}
                <div>
                  <article>
                    <section>
                      <div className={'container'}>
                        <p>{t('client_weGiveFullGuarantee')}</p>
                      </div>
                    </section>
                    <section className={'wide bg-gray-light'}>
                      <div className={'container'}>
                        {/* Client-only form: rendered after mount, not during hydration */}
                        {isMounted && (
                          <GuaranteeForm
                            showModal={notification.showModal}
                            {...formProps}
                          />
                        )}
                      </div>
                    </section>
                  </article>
                </div>
              </RenderBasicTabs>
              {activeTab !== TABS.current.WARRANTY_SERVICE && <ConsultationBlock maxWidth={'1212px'} />}
            </section>
          </main>
          {(common.loading || user.appealLoading || whereBuy.loading || user.appealWait) && <Preloader />}
        </>
      )}
      {common.loading && <Preloader />}
    </div>
  );
};

function mapDispatchToProps(dispatch) {
  return { dispatch };
}

export default connect(null, mapDispatchToProps)(Guarantee);